import { ThreadContentTypes } from 'enums/comments/contentTypes'
import { FilesHelpers } from 'helpers/files'
import { BlobWrapper } from 'interfaces/blobWrapper'
import { ThreadMedia } from 'interfaces/threadMedia'
import { ObservableArray, Subscribable, SubscribableOrNullableValue } from 'knockout'
import AudioPickerManager from 'managers/audioPicker'
import GenericVideoPickerManager from 'managers/genericVideoPicker'
import WCCS3AudioUploader from 'managers/uploads/s3/audio'
import WCCS3FileUploader from 'managers/uploads/s3/file'
import WCCS3ImageUploader from 'managers/uploads/s3/image'
import WCCS3VideoUploader from 'managers/uploads/s3/video'
import { withEffects } from 'mixins/withEffects'
import WCCAttachment from 'models/attachments/attachment'
import WCCFile from 'models/attachments/file'
import WCCImage from 'models/attachments/image'
import WCCVideo from 'models/attachments/video'
import { Topic } from 'models/topic'
import WCCUpload from 'models/upload'

const {
    discussionId,
    allowedUploadImageExtensionsForTopicThreads
} = settings

export interface TopicMediaManagerConfig {
    topic: SubscribableOrNullableValue<Topic>
}

export default class TopicMediaManager {
    private effects = withEffects();

    private topic: Subscribable<Topic | undefined>

    private imagesUploader: WCCS3ImageUploader
    private filesUploader: Subscribable<WCCS3FileUploader | undefined>
    private videoUploader: Subscribable<WCCS3VideoUploader | undefined>
    private audioUploader: WCCS3AudioUploader

    private videoPicker: Subscribable<GenericVideoPickerManager | undefined>
    private audioPicker: AudioPickerManager

    attachments: ObservableArray<WCCAttachment> = ko.observableArray()
    uploads: Subscribable<Array<WCCUpload>>
    media: Subscribable<Array<ThreadMedia>>

    images: Subscribable<Array<WCCImage>>
    files: Subscribable<Array<WCCFile>>
    videos: Subscribable<Array<WCCVideo>>

    isReady: Subscribable<boolean>

    constructor(config: TopicMediaManagerConfig) {
        this.topic = ko.flattenComputed(config.topic);

        const s3VideoKey = this.topic.pluck(topic => discussionId + '/' + topic.id());

        this.imagesUploader = new WCCS3ImageUploader();
        this.filesUploader = this.topic.mapNotNull(topic => new WCCS3FileUploader({ allowMultiple: true, allowedExtensions: topic.allowedAttachmentExtensions() }));
        this.videoUploader = s3VideoKey.mapNotNull(s3VideoKey => new WCCS3VideoUploader({ key: s3VideoKey }));
        this.audioUploader = new WCCS3AudioUploader({ allowMultiple: true });

        this.videoPicker = s3VideoKey.mapNotNull(s3VideoKey => this.effects.register(new GenericVideoPickerManager({ key: s3VideoKey })));
        this.audioPicker = new AudioPickerManager();

        this.uploads = ko.pureComputed(() => {
            return [
                this.imagesUploader.uploads(),
                this.videoUploader()?.uploads() ?? [],
                this.filesUploader()?.uploads() ?? [],
                this.audioUploader.uploads()
            ].reduce((result, uploads) => result.concat(uploads), <Array<WCCUpload>>[]);
        });

        this.media = ko.pureComputed(() => (<Array<ThreadMedia>>this.attachments()).concat(this.uploads()));

        this.images = this.attachments.filter<WCCImage>(a => a.isImage());
        this.videos = this.attachments.filter<WCCVideo>(a => a.isVideo());
        this.files = this.attachments.filter<WCCFile>(a => a.isFile());

        this.isReady = ko.pureComputed(() => this.topic() != undefined);
    }

    pick(type: ThreadContentTypes) {
        switch (type) {
            case 'image':
                this.pickImage();
                break;
            case 'files':
                this.pickFiles();
                break;
            case 'video':
            case 'videoblog':
                this.pickVideo();
                break;
            case 'audio':
                this.pickAudio();
                break;
        }
    }

    async pickImage() {
        const files = await FilesHelpers.pickFiles(allowedUploadImageExtensionsForTopicThreads);
        files.forEach(file => this.uploadImage(file));
    }

    async pickFiles() {
        const topic = this.topic();

        if (topic != undefined) {
            const files = await FilesHelpers.pickFiles(topic.allowedAttachmentExtensions());
            files.forEach(file => this.uploadFile(file));
        }
    }

    pickVideo() {
        this.videoPicker.invokeNotNull(async videoPicker => {
            try {
                const wccVideo = await videoPicker.pick();

                if (wccVideo != undefined)
                    this.attachments.push(wccVideo);
            } catch (ex) {
                system.handleError(ex);
            }
        })
    }

    async pickAudio() {
        try {
            const wccAudio = await this.audioPicker.pick();

            if (wccAudio != undefined)
                this.attachments.push(wccAudio);
        } catch (ex) {
            system.handleError(ex);
        }
    }

    async uploadImage(fileOrBlob: File | BlobWrapper) {
        try {
            const wccImage = await this.imagesUploader.upload(fileOrBlob);

            if (wccImage != undefined)
                this.attachments.push(wccImage);
        } catch (ex) {
            system.handleError(ex);
        }
    }

    uploadFile(fileOrBlob: File | BlobWrapper) {
        this.filesUploader.invokeNotNull(async filesUploader => {
            try {
                const wccFile = await filesUploader.upload(fileOrBlob);

                if (wccFile != undefined)
                    this.attachments.push(wccFile);
            } catch (ex) {
                system.handleError(ex);
            }
        });
    }

    uploadVideo(fileOrBlob: File | BlobWrapper) {
        this.videoUploader.invokeNotNull(async videoUploader => {
            try {
                this.attachments.push(await videoUploader.upload(fileOrBlob));
            } catch (ex) {
                system.handleError(ex);
            }
        });
    }

    async uploadAudio(fileOrBlob: File | BlobWrapper) {
        try {
            this.attachments.push(await this.audioUploader.upload(fileOrBlob));
        } catch (ex) {
            system.handleError(ex);
        }        
    }

    removeAttachment(attachment: WCCAttachment) {
        system.confirm(messages.AreYouSureYouWantToDeleteThisFile, () => this.attachments.remove(attachment));
    }

    dispose() {
        this.effects.dispose();
    }
}